<template>
  <!-- Kontener strony logowania -->
  <div :class="['container', { 'container--sg': shouldHideLocation }]">
    <div class="card m-3 text-center">
      <!-- Nagłówek -->
      <h4 class="card-header">
        {{ isSsoProcessing ? 'Logowanie...' : t('log-in') }}
      </h4>

      <div class="d-flex card-body justify-content-center">
        <!-- 🔄 Widok SSO -->
        <div
            v-if="isSsoProcessing"
            class="d-flex flex-column align-items-center py-4"
        >
          <div class="spinner-border mb-3" role="status">
            <span class="visually-hidden">Logowanie...</span>
          </div>
          <div>Logowanie...</div>
        </div>

        <!-- 🧾 Zwykły formularz logowania -->
        <Form
            v-else
            @submit="onSubmit"
            :validation-schema="schema"
            v-slot="{ errors, isSubmitting }"
        >
          <!-- Pole loginu -->
          <div class="form-group row mb-1">
            <label class="col-sm-2 col-form-label">{{ t('login') }}</label>
            <div class="col-sm-10">
              <Field
                  name="username"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.username }"
              />
              <div class="invalid-feedback">{{ errors.username }}</div>
            </div>
          </div>

          <!-- Pole hasła -->
          <div class="form-group row mb-1">
            <label class="col-sm-2 col-form-label">{{ t('password') }}</label>
            <div class="col-sm-10">
              <Field
                  name="password"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': errors.password }"
              />
              <div class="invalid-feedback">{{ errors.password }}</div>
            </div>
          </div>

          <!-- Link do resetu hasła, przycisk logowania -->
          <div class="form-group row d-flex flex-column align-items-center">
            <div class="col-auto">
              <router-link to="reset-password" class="btn btn-link">
                {{ t('forgot-password') }}
              </router-link>
            </div>
            <div v-if="isSG" class="col-auto">
              <a href="/downloads/mkcert-root-ca.crt" target="_blank" rel="noopener" class="download-link">
                Pobierz certyfikat
              </a>
            </div>
            <div v-if="isSG" class="col-auto">
              <a href="/downloads/instrukcja.pdf" target="_blank" rel="noopener" class="download-link">
                Pobierz instrukcję (PDF)
              </a>
            </div>
            <div class="col-auto">
              <button class="btn btn-outline-secondary" :disabled="isSubmitting">
                <span
                    v-show="isSubmitting"
                    class="spinner-border spinner-border-sm mr-1"
                ></span>
                {{ t('log-in').toUpperCase() }}
              </button>
            </div>
          </div>

          <!-- Komunikat o błędzie logowania -->
          <div
              v-if="errorStore.errorMessage"
              class="alert alert-danger alert-dismissible fade show"
              role="alert"
          >
            {{ errorStore.errorMessage }}
            <button
                type="button"
                class="btn-close"
                data-bs-dismiss="alert"
                aria-label="Close"
            ></button>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { useAuthStore } from "@/stores/auth.store";
import { useUserStore } from "@/stores/user.store";
import { useErrorStore } from "@/stores/error.store";
import { useI18n } from "vue-i18n";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { isSG } from "@/config/appVersion";

const { t } = useI18n();

const errorStore = useErrorStore();
const authStore = useAuthStore();
const router = useRouter();

// 🔄 czy trwa logowanie SSO
// sprawdzamy to OD RAZU, zanim komponent się wyrenderuje
const searchNow = window.location.search || "";
const paramsNow = new URLSearchParams(searchNow);
const initialTicket =
    paramsNow.get("ticket") || sessionStorage.getItem("desktopSsoTicket");

const isSsoProcessing = ref(!!initialTicket);

// schemat walidacji
const schema = Yup.object().shape({
  username: Yup.string().required(t("user-login-required")),
  password: Yup.string().required(t("user-password-required")),
});

// zwykły submit formularza
async function onSubmit(values) {
  const { username, password } = values;
  await authStore.login(username, password);
}

// ---- SSO: automatyczne logowanie po ?ticket=... ----
onMounted(async () => {
  const ticket = initialTicket;

  if (!ticket) {
    // brak biletu – upewnij się, że spinner nie wisi
    isSsoProcessing.value = false;
    return;
  }

  try {
    const res = await fetchWrapper.post(
        `${process.env.VUE_APP_API_URL}/desktop-sso-exchange`,
        { ticket }
    );

    // bilet zużyty – od razu wyczyść
    sessionStorage.removeItem("desktopSsoTicket");

    authStore.token = res.token;
    sessionStorage.setItem("cvToken", res.token);
    errorStore.clearErrorMessage();

    const passwordExpired = await authStore.ensurePasswordFreshness();
    if (passwordExpired) {
      errorStore.showErrorMessage(
          "Twoje hasło straciło ważność. Ustaw nowe hasło, aby kontynuować."
      );
      await router.push("/password-expired");
      return;
    }

    authStore.startInactivityWatch();
    authStore.ping(false);

    // Załaduj profil zanim wybierzemy trasę — `landingPath()` używa ról.
    const userStore = useUserStore();
    try { await userStore.self(); } catch { /* fallback w resolvePostLoginTarget */ }
    const target = authStore.resolvePostLoginTarget(userStore);

    await router.push(target);
  } catch (e) {
    console.error("[SSO] exchange error", e);
    errorStore.showErrorMessage(
        "Nie udało się zalogować za pomocą SSO. Spróbuj zalogować się standardowo."
    );
  } finally {
    // jeśli SSO padło albo skończyło się, wróć do normalnego formularza
    isSsoProcessing.value = false;
  }
});

const shouldHideLocation = computed(() => isSG);
</script>

<style scoped>
/* Kontener z tłem oraz responsywnym środkiem */
.container {
  background-image: url("../assets/login.jpg"); /* obrazek w tle */
  height: 1200px;
  width: 1300px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 150px;
  position: relative;
}

.container--sg {
  background-image: url("../assets/sglogin.jpg"); /* obrazek w tle dla wersji SG */
}

/* Półprzezroczysta warstwa nakładki dla lepszej czytelności formularza */
.container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(241, 241, 241, 0.5);
  z-index: 1;
}

/* Zawartość na wierzchu nakładki */
.container > * {
  position: relative;
  z-index: 2;
}

.card {
  width: 500px;
}

.card-body {
  background: #f5f4f3;
}

.card-body form {
  width: 400px;
}

.col-form-label {
  text-align: right;
  line-height: 25px;
}

/* Stylowanie przycisków i linków */
button {
  border: 0;
  color: #231f20;
  font-size: 15px;
  font-weight: bold;
}
a {
  color: #231f20;
  font-size: 12px;
  text-decoration: none;
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-secondary:hover {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-secondary:focus,
.btn-outline-secondary.focus {
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5);
}

.btn-outline-secondary.disabled,
.btn-outline-secondary:disabled {
  color: #6c757d;
  background-color: transparent;
}

.btn-outline-secondary:not(:disabled):not(.disabled):active,
.btn-outline-secondary:not(:disabled):not(.disabled).active,
.show > .btn-outline-secondary.dropdown-toggle {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-secondary:not(:disabled):not(.disabled):active:focus,
.btn-outline-secondary:not(:disabled):not(.disabled).active:focus,
.show > .btn-outline-secondary.dropdown-toggle:focus {
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5);
}

/* Pola input */
input {
  border: 0;
  border-left: 1px solid #c8c7c7;
  border-top: 1px solid #c8c7c7;
  padding: 10px;
}
input:focus {
  border-color: #c8c7c7;
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}

.alert-danger {
  margin-top: 1rem;
}
</style>
